// AuthContext
//
// Single auth track: Player — Supabase phone OTP. Session lives in the Supabase
// JS client's localStorage; the players-table row is fetched from the
// get-my-player Netlify function on session change.
//
// The legacy commissioner PIN track has been removed (the /event-setup route is
// no longer PIN-gated). Admin/commissioner affordances on the mobile PWA are
// available to any signed-in player; the real role enforcement (who may create
// events) lives server-side. `isCommissioner` is retained as a derived flag so
// existing admin-affordance gates keep working — it now simply means "a
// signed-in player on the on-course commissioner surface."

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { readCache, writeCache, clearCache, TTL } from '../lib/cache'
import { resolvePlayerState } from '../lib/authResume'
import { resolveLinkIdentityUrl } from '../lib/linkIdentityFlow'
import { scopesFor } from '../lib/socialProviders'

const AuthContext = createContext(null)

// Cache the players row so warm cold-boots don't wait on a Netlify
// function invocation for /get-my-player. TTL was 15 min, which was too
// short for longer backgrounds: on a killed-PWA cold start after an
// overnight (or multi-hour) background, an expired cache paints nothing,
// and the App gate then flashes the boot splash (F-050). Bumped to 24h
// (TTL.DAY) so realistic backgrounds still instant-paint. The paint window
// is longer still (cache.js treats an entry as `expired` only past 10x TTL),
// but every read is stale-while-revalidate: the mount + resume fetches, plus
// completeSignup and refreshPlayer, re-write the cache, so a name/handle edit
// still shows promptly on the next reload. localStorage stays a paint cache,
// never a source of truth.
const PLAYER_CACHE_KEY = 'me'
const PLAYER_CACHE_TTL = TTL.DAY

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [player, setPlayer] = useState(() => {
    // Instant-paint from cache on the very first render. Whatever was
    // stored is treated as "good enough to show" until the network
    // fetch below catches up (SWR). Nothing sensitive is here that
    // Supabase's own session storage doesn't already hold.
    const cached = readCache(PLAYER_CACHE_KEY)
    return cached && !cached.expired ? cached.value : null
  })
  const [loading, setLoading] = useState(true)
  const [playerLoading, setPlayerLoading] = useState(false)
  // True ONLY when the server authoritatively told us this auth user has no
  // completed player row (HTTP 200 + data:null) — a brand-new OTP user or a
  // deleted row. The App gate uses this to route to SignupPage only when it is
  // SURE the profile is missing, never on a transient fetch error. Starts false
  // so a cold boot with an instant-paint cached player never flashes SignupPage.
  const [playerMissingConfirmed, setPlayerMissingConfirmed] = useState(false)

  // -- mount: initial session (async) --
  useEffect(() => {
    let mounted = true

    // Apply a fetch outcome to player state + cache. Shared by both the mount
    // cold-start path and the onAuthStateChange resume path so the resilience
    // rules live in exactly one place (resolvePlayerState). cache action and
    // confirmedAbsent don't depend on the prior player, so they're computed
    // directly; the kept-player value needs the true prior, read via the
    // functional setState updater to dodge a stale closure.
    const applyPlayerOutcome = (sess, outcome) => {
      const { cache, confirmedAbsent } = resolvePlayerState({
        session: sess, fetchOutcome: outcome, priorPlayer: null,
      })
      setPlayer((prior) =>
        resolvePlayerState({ session: sess, fetchOutcome: outcome, priorPlayer: prior }).player
      )
      setPlayerMissingConfirmed(confirmedAbsent)
      if (cache === 'write') writeCache(PLAYER_CACHE_KEY, outcome.player, PLAYER_CACHE_TTL)
      else if (cache === 'clear') clearCache(PLAYER_CACHE_KEY)
      // 'keep' → leave the last-good cache untouched (transient failure).
    }

    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return
      let sess = data.session || null
      setSession(sess)
      if (sess) {
        let outcome = await fetchPlayer(sess.access_token)
        // Retry ONLY a transient failure (network/5xx/401-in-rotation). An
        // authoritative 'absent' (200 + no row) needs no retry — the answer
        // won't change. On a phone this mount path is what a killed-PWA cold
        // start runs, so the same resilience the resume path has must apply
        // here or a logged-in user lands on SignupPage after backgrounding.
        if (outcome.status === 'error' && mounted) {
          const { data: refreshData } = await supabase.auth.refreshSession()
          if (refreshData?.session) {
            sess = refreshData.session
            setSession(sess)
            outcome = await fetchPlayer(sess.access_token)
          }
        }
        if (mounted) applyPlayerOutcome(sess, outcome)
      }
      if (mounted) setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      if (!mounted) return
      setSession(sess || null)
      if (sess) {
        if (mounted) setPlayerLoading(true)
        let outcome = await fetchPlayer(sess.access_token)
        // Resume resilience: a transient failure WITH a live session (a cold-
        // started get-my-player function, a brief network drop, or a 401 during
        // token rotation on a background→foreground TOKEN_REFRESHED) must not
        // clear the player. Retry once on a transient error only; an
        // authoritative 'absent' won't change on retry.
        if (outcome.status === 'error' && mounted) {
          const { data: refreshData } = await supabase.auth.refreshSession()
          if (refreshData?.session) {
            sess = refreshData.session
            setSession(sess)
            outcome = await fetchPlayer(sess.access_token)
          }
        }
        if (mounted) {
          applyPlayerOutcome(sess, outcome)
          setPlayerLoading(false)
        }
      } else {
        clearCache(PLAYER_CACHE_KEY)
        setPlayer(null)
        setPlayerMissingConfirmed(false)
        setPlayerLoading(false)
      }
    })

    return () => {
      mounted = false
      sub.subscription.unsubscribe()
    }
  }, [])

  // Returns a discriminated outcome, not a bare player, so callers can tell an
  // AUTHORITATIVE "no row" (HTTP 200 + data:null — brand-new user or deleted
  // row) apart from a TRANSIENT failure (network throw / non-OK HTTP). Only the
  // former may clear a logged-in user; the latter must keep the last-good
  // player. See resolvePlayerState in src/lib/authResume.js.
  const fetchPlayer = async (token) => {
    try {
      const res = await fetch('/.netlify/functions/get-my-player', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return { status: 'error', player: null }
      const json = await res.json()
      return json?.data
        ? { status: 'found', player: json.data }
        : { status: 'absent', player: null }
    } catch {
      return { status: 'error', player: null }
    }
  }

  // -- Player (phone OTP) --

  const signInWithPhone = async (phone) => {
    const { error } = await supabase.auth.signInWithOtp({ phone })
    return error?.message || null
  }

  const verifyOtp = async (phone, code) => {
    const { error } = await supabase.auth.verifyOtp({ phone, token: code, type: 'sms' })
    return error?.message || null
  }

  // -- Social sign-in (Facebook / X; Google wired but hidden per D-283) --
  //
  // Provider-agnostic: both calls forward whatever provider string the shared
  // ENABLED_SOCIAL list (src/lib/socialProviders.js) renders a button for.
  // ADDITIVE to phone OTP, which stays PRIMARY. Two distinct entry points:
  //
  //   signInWithOAuth  — used from the LOGIN surface by a user who is NOT signed
  //     in. Redirects the browser to the provider; on return, supabase.js's
  //     detectSessionInUrl parses the tokens, a session is established, and
  //     onAuthStateChange (above) fetches the player row. A brand-new social
  //     user with no players row lands on SignupPage, whose complete-signup call
  //     now matches/creates by email when no phone is present.
  //
  //   prepareLinkIdentity — used from the PROFILE surface by a user ALREADY
  //     signed in via phone. Attaches the social provider to the SAME auth.users record
  //     (one auth_user_id, multiple identities) so the social profile picture
  //     can be pulled WITHOUT minting a second auth_user_id (players.auth_user_id
  //     is UNIQUE — a second one would 23505 or orphan the row). This is the
  //     single-table-identity-safe way to cross-link providers.
  //
  // supabase-js runs on the CLIENT only (browsers have native WebSocket); these
  // calls never touch a Netlify function (CLAUDE.md WebSocket/Node20 trap).
  // Provider-agnostic scope handling: read the optional per-provider scopes from
  // socialProviders (Facebook => 'public_profile' — the owner did NOT configure
  // the FB `email` permission, so the Supabase-default email scope errors with
  // "Invalid Scopes: email"). Attach options.scopes ONLY when defined; omit it
  // for providers with no override so Supabase applies its default (unchanged).
  const signInWithOAuth = async (provider) => {
    const options = { redirectTo: window.location.origin }
    const scopes = scopesFor(provider)
    if (scopes) options.scopes = scopes
    const { error } = await supabase.auth.signInWithOAuth({ provider, options })
    return error?.message || null
  }

  // prepareLinkIdentity — the mobile-safe entry point ProfileEditPage uses.
  // It runs the authenticated linkIdentity call and RETURNS { url, error }
  // WITHOUT navigating, so the profile page can render a real <a href> the user
  // taps to reach the provider. The mobile link flow MUST go through this
  // same-origin-anchor bridge — NEVER a direct programmatic window.location to
  // the provider: a redirect after this async call is silently blocked by
  // mobile browsers' anti-tracking (proven on-device); a genuine user tap on
  // the returned URL is not. See resolveLinkIdentityUrl for the full root-cause
  // note. redirectTo returns the user to the Edit-Profile surface
  // (/profile-edit) after the provider round-trips — that's where the
  // Linked-Accounts / Unlink UI and the pulled avatar live, so the user lands
  // back where they started. NOT the Supabase project Site URL (marketing site).
  const prepareLinkIdentity = async (provider) =>
    resolveLinkIdentityUrl({
      provider,
      redirectTo: `${window.location.origin}/profile-edit`,
      scopes: scopesFor(provider),
      linkFn: (args) => supabase.auth.linkIdentity(args),
    })

  const completeSignup = async (fields) => {
    if (!session?.access_token) return { error: 'No session' }
    try {
      const res = await fetch('/.netlify/functions/complete-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(fields),
      })
      const json = await res.json()
      if (!res.ok) return { error: json.error || 'Signup failed' }
      setPlayer(json.data)
      if (json.data) {
        setPlayerMissingConfirmed(false)
        writeCache(PLAYER_CACHE_KEY, json.data, PLAYER_CACHE_TTL)
      }
      return { player: json.data }
    } catch (e) {
      return { error: e.message }
    }
  }

  const signOutAccount = async () => {
    await supabase.auth.signOut()
    clearCache(PLAYER_CACHE_KEY)
    setSession(null)
    setPlayer(null)
    setPlayerMissingConfirmed(false)
  }

  // Revoke a pending account-deletion request (Apple 5.1.1(v) restore flow).
  // Calls restore-account (clears players.deletion_requested_at server-side)
  // then re-fetches the player so the cleared flag lands in state + cache and
  // the App restore-gate (shouldShowRestoreGate) lets the user through. The
  // player object carries deletion_requested_at because get-my-player SELECTs *.
  const restoreAccount = async () => {
    if (!session?.access_token) return { error: 'No session' }
    try {
      const res = await fetch('/.netlify/functions/restore-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) return { error: json.error || 'Restore failed' }
      await refreshPlayer()
      return { ok: true }
    } catch (e) {
      return { error: e.message }
    }
  }

  const refreshPlayer = async () => {
    if (!session) return
    const outcome = await fetchPlayer(session.access_token)
    // Only overwrite on a real fetched player; a transient failure or an
    // authoritative-absent here leaves the current player untouched (matches
    // the prior behavior of an explicit manual refresh).
    if (outcome.status === 'found' && outcome.player) {
      setPlayer(outcome.player)
      setPlayerMissingConfirmed(false)
      writeCache(PLAYER_CACHE_KEY, outcome.player, PLAYER_CACHE_TTL)
    }
  }

  const isSignedUp = !!(player?.tos_accepted_at)

  return (
    <AuthContext.Provider value={{
      // state
      session, player, loading, playerLoading, playerMissingConfirmed,
      // computed
      isPlayer: !!player,
      isSignedUp,
      // Derived admin-affordance flag (see header note). A signed-in player on
      // the mobile surface has access to the playing-commissioner controls.
      isCommissioner: !!player,
      // player auth
      signInWithPhone, verifyOtp, completeSignup, signOutAccount, refreshPlayer,
      // Apple 5.1.1(v) account-deletion restore: revoke a pending deletion on
      // sign-in. The delete side is a direct authed call from MePage; restore
      // routes through here so it can re-fetch the cleared player into state.
      restoreAccount,
      // social auth (additive; phone OTP stays primary). Provider-agnostic —
      // Facebook / X today, Google wired-but-hidden (D-283, socialProviders.js).
      // prepareLinkIdentity is the mobile-safe two-phase link entry point
      // (returns { url, error } for a user-tapped anchor). The old auto-navigate
      // linkIdentity path was removed — its post-async window.location redirect
      // was silently blocked on mobile (the original bug this flow fixed).
      signInWithOAuth, prepareLinkIdentity,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
